import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import AmpliarMixModal from "@/components/AmpliarMixModal";

// LOTE X-1 F2 — modal único com 3 variações baseadas nas flags do payload
// de 409 duplicado (ver `payload_duplicado` em backend/leads.py):
//
//   pode_ampliar_mix        → "Este já é seu cliente — Ampliar mix"
//   pode_abrir_ficha        → "Este já é seu cliente (não-Ativo) — Abrir ficha"
//   pode_avisar_responsavel → "Cliente de outro vendedor — Avisar o responsável"
//
// O backend decide; o front só exibe/encaminha.
export default function DuplicadoCnpjModal({
  open, onOpenChange, dup, cnpjConsultado, razaoConsultada,
}) {
  const navigate = useNavigate();
  const [observacao, setObservacao] = useState("");
  const [busy, setBusy] = useState(false);
  const [ampliarOpen, setAmpliarOpen] = useState(false);
  if (!open || !dup) return null;

  const titulo = dup.pode_ampliar_mix
    ? "Este cliente já é seu"
    : dup.pode_abrir_ficha
      ? "Este cliente já está na sua carteira"
      : dup.efetivado
        ? `Cliente da carteira de ${dup.responsavel_primeiro_nome || "outro vendedor"}`
        : `Em prospecção por ${dup.responsavel_primeiro_nome || "outro vendedor"}`;

  const subtitulo = dup.pode_abrir_ficha
    ? `Status: ${dup.situacao || "—"}.`
    : (!dup.pode_ampliar_mix && dup.expira_em_br)
      ? `Libera em ${dup.expira_em_br}.`
      : "";

  const avisar = async () => {
    setBusy(true);
    try {
      await api.post("/clientes/avisar-responsavel", {
        cnpj: cnpjConsultado,
        razao_social: razaoConsultada || dup.razao_social_consultada || dup.nome_cadastrado,
        observacao,
      });
      toast.success(`Avisamos ${dup.responsavel_primeiro_nome || "o responsável"}. A mensagem chegou no cliente.`);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e) || "Falha ao avisar o responsável");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="dup-cnpj-modal">
        <div className="bg-white w-full md:max-w-md rounded-t-2xl md:rounded-2xl p-5 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-navy" data-testid="dup-cnpj-titulo">{titulo}</h2>
            <button onClick={() => onOpenChange(false)} data-testid="dup-cnpj-close" className="text-slate-400 hover:text-navy text-xl leading-none" aria-label="Fechar">×</button>
          </div>
          <div className="mb-4 text-sm text-slate-600">
            <div><span className="font-medium text-navy">{dup.nome_cadastrado}</span> {dup.cnpj_formatado ? <span className="text-xs text-muted-foreground">· CNPJ {dup.cnpj_formatado}</span> : null}</div>
            {subtitulo ? <div className="text-xs text-muted-foreground mt-0.5">{subtitulo}</div> : null}
          </div>

          {dup.pode_avisar_responsavel && (
            <div className="mb-4">
              <label className="text-xs font-medium text-muted-foreground">Observação (opcional)</label>
              <textarea
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                data-testid="dup-cnpj-observacao"
                maxLength={400}
                rows={3}
                placeholder="Ex.: Tenho um contato na área de compras, posso ajudar?"
                className="w-full mt-1 rounded-lg border border-input bg-white px-3 py-2 text-sm"
              />
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={() => onOpenChange(false)} disabled={busy}
              data-testid="dup-cnpj-fechar"
              className="flex-1 h-10 rounded-lg border border-input bg-white text-slate-600 text-sm font-semibold">
              Fechar
            </button>
            {dup.pode_ampliar_mix && dup.cliente_id && (
              <button onClick={() => setAmpliarOpen(true)} disabled={busy}
                data-testid="dup-cnpj-ampliar"
                className="flex-1 h-10 rounded-lg bg-brand text-white text-sm font-semibold disabled:opacity-60">
                Ampliar mix
              </button>
            )}
            {dup.pode_abrir_ficha && dup.cliente_id && (
              <button onClick={() => { navigate(`/cliente/${dup.cliente_id}`); onOpenChange(false); }} disabled={busy}
                data-testid="dup-cnpj-abrir-ficha"
                className="flex-1 h-10 rounded-lg bg-brand text-white text-sm font-semibold disabled:opacity-60">
                Abrir ficha
              </button>
            )}
            {dup.pode_avisar_responsavel && (
              <button onClick={avisar} disabled={busy}
                data-testid="dup-cnpj-avisar"
                className="flex-1 h-10 rounded-lg bg-brand text-white text-sm font-semibold disabled:opacity-60">
                {busy ? "Avisando…" : "Avisar o responsável"}
              </button>
            )}
          </div>
        </div>
      </div>
      {dup.pode_ampliar_mix && dup.cliente_id && (
        <AmpliarMixModal
          open={ampliarOpen}
          onOpenChange={(v) => { setAmpliarOpen(v); if (!v) onOpenChange(false); }}
          cliente={{ id: dup.cliente_id, nome: dup.nome_cadastrado }}
        />
      )}
    </>
  );
}
